import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { appConfig } from '../config';

/**
 * ThemeContext - Manages light/dark theme state across the application
 * 
 * Provides:
 * - theme: 'light' | 'dark'
 * - toggleTheme: Function to switch between themes
 * - themeColors: Object with current theme color values
 */

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme color definitions
 * Option 1: Clean & Minimal Light Theme
 */
const themeColors = {
  light: {
    // Backgrounds
    background: '#fafafa',
    card: '#ffffff',
    sidebar: '#ffffff',
    header: '#ffffff',
    
    // Borders
    border: '#e5e7eb', // gray-200
    borderSubtle: '#f3f4f6', // gray-100
    divider: '#e5e7eb',
    
    // Text
    textPrimary: '#111827', // gray-900
    textSecondary: '#6b7280', // gray-500
    textTertiary: '#9ca3af', // gray-400
    textMuted: '#d1d5db', // gray-300
    
    // Accents
    accent: '#0A4872', // brand blue
    accentHover: '#083d5f', // darker blue
    accentLight: '#e0f2fe', // blue-50
    
    // Special elements
    lessonHeader: '#ffffff',
    todoSidebar: '#ffffff',
    excerptBox: '#f9fafb', // gray-50
  },
  dark: {
    // Backgrounds
    background: '#191919',
    card: '#1f1f1f',
    sidebar: '#202020',
    header: '#1a1a1a',
    
    // Borders
    border: '#2a2a2a',
    borderSubtle: '#333333',
    divider: '#2a2a2a',
    
    // Text
    textPrimary: '#ffffff',
    textSecondary: '#d1d5db', // gray-300
    textTertiary: '#9ca3af', // gray-400
    textMuted: '#6b7280', // gray-500
    
    // Accents
    accent: '#0A4872', // brand blue
    accentHover: '#083d5f',
    accentLight: '#1e3a5f',
    
    // Special elements
    lessonHeader: '#1a1a1a',
    todoSidebar: '#252525',
    excerptBox: '#1f1f1f',
  }
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : 'dark';
  });

  // Apply theme to document root for CSS variables (if needed)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const value = useMemo(() => {
    const baseColors = themeColors[theme];
    const orgBranding = JSON.parse(localStorage.getItem('orgBranding') || '{}');
    const accent = orgBranding.primaryColor || appConfig.primaryColor;
    return {
      theme,
      toggleTheme,
      themeColors: { ...baseColors, accent, accentHover: accent },
      orgBranding,
      isDark: theme === 'dark',
      isLight: theme === 'light'
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
